import React, { useState, useEffect } from "react";
import { X, Save, Loader, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { getImageUrl } from "../utils/imageHelper";
import { getImageUploadSettings } from "../services/settingsApi";

// Default fallback configuration
const DEFAULT_IMAGE_CONFIG = {
  width: 600,
  height: 600,
  maxBytes: 1024 * 1024, // 1MB
};

/**
 * Resize image to specified dimensions and return File + preview data URL
 */
const resizeImage = (file, { width, height, maxBytes }) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        // Calculate scale to cover the canvas (crop to fit)
        const scale = Math.max(width / img.width, height / img.height);
        const dx = (width - img.width * scale) / 2;
        const dy = (height - img.height * scale) / 2;

        // Fill with white background
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, width, height);
        
        // Draw image centered and scaled
        ctx.drawImage(img, dx, dy, img.width * scale, img.height * scale);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Could not process image"));
            if (blob.size > maxBytes) {
              return reject(new Error(`Image must be under ${(maxBytes / 1024).toFixed(0)}KB`));
            }
            const resizedFile = new File([blob], file.name, { 
              type: blob.type || file.type || "image/jpeg" 
            });
            resolve({ file: resizedFile, preview: canvas.toDataURL(blob.type || "image/jpeg") });
          },
          file.type || "image/jpeg",
          0.92
        );
      };
      img.onerror = () => reject(new Error("Unable to read image"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });

// Field components moved outside to prevent recreation on every render
const InputField = ({ label, name, type = "text", placeholder = "", unit = "", value, onChange }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">{label}</label>
    <div className="relative">
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        step={type === "number" ? "0.1" : undefined}
        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#6b4226] focus:border-transparent text-xs h-7"
      />
      {unit && <span className="absolute right-2 top-1 text-[10px] text-gray-500">{unit}</span>}
    </div>
  </div>
);

const TextAreaField = ({ label, name, rows = 3, placeholder = "", value, onChange }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">{label}</label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#6b4226] focus:border-transparent text-xs resize-y leading-tight"
    />
  </div>
);

const CheckboxField = ({ label, name, checked, onChange }) => (
  <div className="flex items-center">
    <input
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      className="h-3.5 w-3.5 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
    />
    <label className="ml-2 text-xs font-medium text-gray-700">{label}</label>
  </div>
);

const ItemEditModal = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    description: "",
    calorie_serve: "",
    protein: "",
    fat: "",
    carbs: "",
    sugar: "",
    sat_fat: "",
    trans_fat: "",
    sodium: "",
    fiber: "",
    health_benefits: "",
    suitable_for: "",
    food_pairing: "",
    mood: "",
    is_signature: false,
    is_healthy: false,
    dietary_type: "",
    status: "Active",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Image handling state
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Image upload settings
  const [imageConfig, setImageConfig] = useState(DEFAULT_IMAGE_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Fetch image upload settings on mount
  useEffect(() => {
    const fetchImageSettings = async () => {
      try {
        const response = await getImageUploadSettings();
        const settings = response.data.data;
        setImageConfig({
          width: settings.image_default_width,
          height: settings.image_default_height,
          maxBytes: settings.image_max_size_bytes,
        });
        setConfigLoaded(true);
      } catch (error) {
        console.warn("Failed to fetch image upload settings, using defaults:", error);
        setImageConfig(DEFAULT_IMAGE_CONFIG);
        setConfigLoaded(true);
      }
    };

    fetchImageSettings();
  }, []);

  useEffect(() => {
    if (item) {
      setFormData({
        description: item.description || "",
        calorie_serve: item.calorie_serve || "",
        protein: item.protein || "",
        fat: item.fat || "",
        carbs: item.carbs || "",
        sugar: item.sugar || "",
        sat_fat: item.sat_fat || "",
        trans_fat: item.trans_fat || "",
        sodium: item.sodium || "",
        fiber: item.fiber || "",
        health_benefits: item.health_benefits || "",
        suitable_for: item.suitable_for || "",
        food_pairing: item.food_pairing || "",
        mood: item.mood || "",
        is_signature: item.is_signature || false,
        is_healthy: item.is_healthy || false,
        dietary_type: item.dietary_type || "",
        status: item.status || "Active",
      });
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle image file selection with dynamic settings
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file (JPEG, PNG, or WebP)");
      return;
    }

    try {
      setError("");
      
      // Resize image to configured dimensions
      const { file: resizedFile, preview } = await resizeImage(file, imageConfig);
      
      setSelectedFile(resizedFile);
      setImagePreview(preview);
      setRemoveImage(false);
      
      console.log(`✅ Image resized to ${imageConfig.width}×${imageConfig.height}, size: ${(resizedFile.size / 1024).toFixed(1)}KB`);
    } catch (err) {
      console.error("Image processing error:", err);
      setSelectedFile(null);
      setImagePreview(null);
      setError(err.message || `Image must be ${imageConfig.width}×${imageConfig.height} and under ${(imageConfig.maxBytes / 1024).toFixed(0)}KB`);
    }
  };

  // Handle image removal
  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setRemoveImage(true); // Flag for deletion on save
    setShowDeleteConfirm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      // Create FormData for multipart submission
      const formDataToSend = new FormData();

      // Add all text and numeric fields
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("calorie_serve", formData.calorie_serve || "");
      formDataToSend.append("protein", formData.protein || "");
      formDataToSend.append("fat", formData.fat || "");
      formDataToSend.append("carbs", formData.carbs || "");
      formDataToSend.append("sugar", formData.sugar || "");
      formDataToSend.append("sat_fat", formData.sat_fat || "");
      formDataToSend.append("trans_fat", formData.trans_fat || "");
      formDataToSend.append("sodium", formData.sodium || "");
      formDataToSend.append("fiber", formData.fiber || "");
      formDataToSend.append("health_benefits", formData.health_benefits || "");
      formDataToSend.append("suitable_for", formData.suitable_for || "");
      formDataToSend.append("food_pairing", formData.food_pairing || "");
      formDataToSend.append("mood", formData.mood || "");
      formDataToSend.append("is_signature", formData.is_signature.toString());
      formDataToSend.append("is_healthy", formData.is_healthy.toString());
      formDataToSend.append("dietary_type", formData.dietary_type || "");
      formDataToSend.append("status", formData.status || "");

      // Handle image upload or removal
      if (selectedFile) {
        formDataToSend.append("image", selectedFile);
        console.log("✅ Image file attached:", {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
        });
      } else if (removeImage) {
        formDataToSend.append("remove_image", "true");
        console.log("🗑️ Image marked for removal");
      }

      // Debug: Log FormData contents properly
      console.log("📦 FormData contents:");
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}:`, `[File: ${value.name}, ${value.size} bytes]`);
        } else {
          console.log(`  ${key}:`, value);
        }
      }

      // Save item data
      await onSave(item.id, formDataToSend);

      onClose();
    } catch (err) {
      console.error("Error saving item:", err);
      setError(err?.response?.data?.message || "Failed to save item. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-[#fffaf5]">
          <div>
            <h2 className="text-base font-bold text-gray-900">Edit Item</h2>
            <p className="text-xs text-gray-600">{item.name}</p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 transition focus:outline-none focus:ring-2 focus:ring-[#6b4226] rounded-full p-1"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-2.5">
            {/* Top Section: Image + Description + Classification */}
            <section className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3 pb-2.5 border-b border-gray-200">
              {/* Product Image */}
              <div>
                <label className="block text-xs font-semibold text-[#6b4226] mb-1.5">Product Image</label>

                {/* Image Preview Area */}
                {(imagePreview || (item.image_url && !removeImage)) && (
                  <div className="mb-1.5 relative inline-block group">
                    <img
                      src={imagePreview || getImageUrl(item.image_url)}
                      alt={item.name}
                      className="h-28 w-28 rounded-lg object-cover border-2 border-gray-300 shadow-sm"
                    />

                    {/* Delete button overlay */}
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition shadow-md opacity-0 group-hover:opacity-100 cursor-pointer"
                      title="Remove image"
                    >
                      <Trash2 size={12} />
                    </button>

                    {/* "New" badge for preview */}
                    {imagePreview && (
                      <span className="absolute bottom-1 left-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold shadow">
                        New
                      </span>
                    )}
                  </div>
                )}

                {/* No image placeholder */}
                {!imagePreview && !item.image_url && !removeImage && (
                  <div className="mb-1.5 h-28 w-28 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50">
                    <ImageIcon size={20} className="text-gray-400 mb-1" />
                    <span className="text-[10px] text-gray-500">No image</span>
                  </div>
                )}

                {/* Image removed state */}
                {removeImage && !imagePreview && (
                  <div className="mb-1.5 h-28 w-28 rounded-lg border-2 border-dashed border-red-300 flex flex-col items-center justify-center bg-red-50">
                    <X size={20} className="text-red-400 mb-1" />
                    <span className="text-[10px] text-red-600 font-semibold">Will be removed</span>
                  </div>
                )}

                {/* File input */}
                <div>
                  <label
                    htmlFor="image-upload"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-[#6b4226] rounded hover:bg-[#58341f] transition cursor-pointer focus-within:ring-2 focus-within:ring-[#6b4226] focus-within:ring-offset-1"
                  >
                    <Upload size={12} />
                    {selectedFile || item.image_url ? "Change" : "Upload"}
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  {/* Show disclaimer only when no image is uploaded */}
                  {!imagePreview && (!item.image_url || removeImage) && (
                    <p className="text-[10px] text-gray-500 mt-1 leading-tight">
                      {configLoaded ? (
                        <>
                          {imageConfig.width}×{imageConfig.height}px, max {(imageConfig.maxBytes / 1024).toFixed(0)}KB
                        </>
                      ) : (
                        "Loading..."
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* Description + Classification */}
              <div className="space-y-2">
                <label className="block text-[11px] font-semibold text-[#6b4226] mb-1">Description</label>
                <TextAreaField
                  label=""
                  name="description"
                  rows={4}
                  placeholder="Enter item description..."
                  value={formData.description}
                  onChange={handleChange}
                />
                
                {/* Item Classification + Status inline */}
                <div className="pt-1 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1">
                    <label className="block text-[11px] font-semibold text-[#6b4226] mb-1">
                      Classification
                    </label>
                    <div className="flex flex-wrap items-center gap-4">
                      <CheckboxField
                        label="Signature Item"
                        name="is_signature"
                        checked={formData.is_signature}
                        onChange={handleChange}
                      />
                      <CheckboxField
                        label="Healthy Option"
                        name="is_healthy"
                        checked={formData.is_healthy}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Status (Enable / Disable) */}
                  <div className="md:w-auto">
                    <label className="block text-[11px] font-semibold text-[#6b4226] mb-1">
                      Status
                    </label>
                    <div className="inline-flex rounded-full border border-gray-300 bg-white overflow-hidden text-[11px]">
                      {[
                        { key: "Active", label: "Active", helper: "Visible in chatbot" },
                        { key: "Inactive", label: "Inactive", helper: "Hidden from chatbot" },
                      ].map((opt) => {
                        const isSelected = formData.status === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                status: opt.key,
                              }))
                            }
                            className={`px-2.5 py-1 flex flex-col items-start border-l border-gray-200 first:border-l-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#6b4226] focus-visible:z-10 transition ${
                              isSelected
                                ? "bg-green-50 text-green-800"
                                : "bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <span className="font-semibold leading-tight">{opt.label}</span>
                            <span className="text-[10px] leading-tight text-gray-500">
                              {opt.helper}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Nutritional Information */}
            <section className="py-2 border-b border-gray-200">
              <h3 className="text-sm font-bold text-[#6b4226] mb-1.5 uppercase tracking-wide">Nutritional Information</h3>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-x-2.5 gap-y-1.5">
                <InputField
                  label="Cal/Serve"
                  name="calorie_serve"
                  type="number"
                  placeholder="0"
                  unit="kcal"
                  value={formData.calorie_serve}
                  onChange={handleChange}
                />
                <InputField 
                  label="Protein" 
                  name="protein" 
                  type="number" 
                  placeholder="0" 
                  unit="g" 
                  value={formData.protein}
                  onChange={handleChange}
                />
                <InputField 
                  label="Fat" 
                  name="fat" 
                  type="number" 
                  placeholder="0" 
                  unit="g" 
                  value={formData.fat}
                  onChange={handleChange}
                />
                <InputField 
                  label="Carbs" 
                  name="carbs" 
                  type="number" 
                  placeholder="0" 
                  unit="g" 
                  value={formData.carbs}
                  onChange={handleChange}
                />
                <InputField 
                  label="Sugar" 
                  name="sugar" 
                  type="number" 
                  placeholder="0" 
                  unit="g" 
                  value={formData.sugar}
                  onChange={handleChange}
                />
                <InputField 
                  label="Sat Fat" 
                  name="sat_fat" 
                  type="number" 
                  placeholder="0" 
                  unit="g" 
                  value={formData.sat_fat}
                  onChange={handleChange}
                />
                <InputField
                  label="Trans Fat"
                  name="trans_fat"
                  type="number"
                  placeholder="0"
                  unit="g"
                  value={formData.trans_fat}
                  onChange={handleChange}
                />
                <InputField 
                  label="Sodium" 
                  name="sodium" 
                  type="number" 
                  placeholder="0" 
                  unit="mg" 
                  value={formData.sodium}
                  onChange={handleChange}
                />
                <InputField 
                  label="Fiber" 
                  name="fiber" 
                  type="number" 
                  placeholder="0" 
                  unit="g" 
                  value={formData.fiber}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Additional Details */}
            <section className="py-2">
              <h3 className="text-sm font-bold text-[#6b4226] mb-1.5 uppercase tracking-wide">Additional Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <TextAreaField
                  label="Health Benefits"
                  name="health_benefits"
                  rows={2}
                  placeholder="e.g., Rich in antioxidants..."
                  value={formData.health_benefits}
                  onChange={handleChange}
                />
                <TextAreaField
                  label="Suitable For"
                  name="suitable_for"
                  rows={2}
                  placeholder="e.g., Vegan, Gluten-free..."
                  value={formData.suitable_for}
                  onChange={handleChange}
                />
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                    Dietary Type
                  </label>
                  <select
                    name="dietary_type"
                    value={formData.dietary_type}
                    onChange={handleChange}
                    className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#6b4226] focus:border-transparent text-xs h-7 bg-white"
                  >
                    <option value="">Not Set</option>
                    <option value="Veg">Veg</option>
                    <option value="NonVeg">NonVeg</option>
                    <option value="Vegan">Vegan</option>
                  </select>
                </div>
                <TextAreaField
                  label="Food Pairing"
                  name="food_pairing"
                  rows={2}
                  placeholder="e.g., Goes well with..."
                  value={formData.food_pairing}
                  onChange={handleChange}
                />
                <TextAreaField
                  label="Mood"
                  name="mood"
                  rows={2}
                  placeholder="e.g., Energizing, Relaxing..."
                  value={formData.mood}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Error Message */}
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-2 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#6b4226] text-white rounded hover:bg-[#58341f] transition focus:outline-none focus:ring-2 focus:ring-[#6b4226] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader className="animate-spin" size={14} />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete Image Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-5 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-0.5">Remove Image?</h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  This will remove the product image when you save. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveImage}
                className="px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemEditModal;
